import { useEffect, useRef, useState } from "react";
import { CheckCircle2, Loader2, XCircle, Heart } from "lucide-react";
import { api } from "../lib/api";

const MAX_ATTEMPTS = 6;
const POLL_INTERVAL = 2000;

function cleanUrl() {
  if (window.history?.replaceState) {
    window.history.replaceState({}, document.title, window.location.pathname);
  }
}

export default function DonationReturnHandler() {
  const [state, setState] = useState({ visible: false, phase: "idle", message: "", amount: null });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const donation = params.get("donation");
    const sessionId = params.get("session_id");

    if (donation === "cancelled") {
      startedRef.current = true;
      setState({
        visible: true,
        phase: "cancelled",
        message: "Your donation was cancelled. Whenever you're ready, we're here.",
        amount: null,
      });
      cleanUrl();
      return;
    }

    if (donation === "success" && sessionId) {
      startedRef.current = true;
      setState({ visible: true, phase: "pending", message: "Confirming your gift…", amount: null });

      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const data = await api.getDonationStatus(sessionId);
          if (data.payment_status === "paid") {
            setState({
              visible: true,
              phase: "paid",
              message: "Thank you — your gift has been received.",
              amount: data.amount,
            });
            cleanUrl();
            return;
          }
          if (data.status === "expired") {
            setState({
              visible: true,
              phase: "error",
              message: "This donation session expired. Please try again.",
              amount: null,
            });
            cleanUrl();
            return;
          }
          if (attempts >= MAX_ATTEMPTS) {
            // Stripe only redirects to the success URL after the customer
            // completes payment, so reaching this point is a strong signal
            // the gift went through even if status polling lags. Show soft
            // success; webhooks remain the source of truth server-side.
            setState({
              visible: true,
              phase: "paid",
              message:
                "Thank you — your gift is being finalized. You'll receive a receipt from Stripe shortly.",
              amount: data?.amount ?? null,
            });
            cleanUrl();
            return;
          }
          setTimeout(poll, POLL_INTERVAL);
        } catch (err) {
          if (attempts >= MAX_ATTEMPTS) {
            setState({
              visible: true,
              phase: "paid",
              message:
                "Thank you — your gift is being finalized. You'll receive a receipt from Stripe shortly.",
              amount: null,
            });
            cleanUrl();
            return;
          }
          setTimeout(poll, POLL_INTERVAL);
        }
      };
      poll();
    }
  }, []);

  if (!state.visible) return null;

  const Icon =
    state.phase === "paid"
      ? CheckCircle2
      : state.phase === "pending"
      ? Loader2
      : state.phase === "cancelled"
      ? Heart
      : XCircle;

  const accent =
    state.phase === "paid"
      ? "text-[#2A4747]"
      : state.phase === "error"
      ? "text-[#8B2635]"
      : "text-[#692128]";

  return (
    <div
      data-testid="donation-return-handler"
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 sm:p-6"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <div className="relative w-full max-w-md bg-[#FAF8F5] rounded-3xl border border-[#E2DCD0] shadow-2xl p-8 text-center">
        <div className={`w-14 h-14 mx-auto mb-5 rounded-full bg-white flex items-center justify-center ${accent}`}>
          <Icon size={26} className={state.phase === "pending" ? "animate-spin" : ""} />
        </div>
        <h3 className="font-serif text-2xl sm:text-3xl text-[#2C2A29]">
          {state.phase === "paid" && "Thank you."}
          {state.phase === "pending" && "Confirming your gift…"}
          {state.phase === "cancelled" && "No gift today."}
          {state.phase === "error" && "Hmm, something went wrong."}
        </h3>
        <p className="mt-3 text-[#5C5856] leading-relaxed">{state.message}</p>
        {state.phase === "paid" && state.amount && (
          <p className="mt-2 font-serif text-2xl text-[#692128]">${state.amount.toFixed(2)}</p>
        )}
        {state.phase !== "pending" && (
          <button
            onClick={() => setState({ ...state, visible: false })}
            data-testid="donation-return-close"
            className="mt-7 inline-flex items-center gap-2 bg-[#692128] text-[#F4F1EB] hover:bg-[#8B2635] rounded-full px-7 py-3 text-sm font-semibold tracking-wide transition-all"
          >
            Close
          </button>
        )}
      </div>
    </div>
  );
}
